import React, { useEffect, useMemo, useState } from "react";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { orderBy } from "@progress/kendo-data-query";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import APP_ROUTES from "../../../helper/app-routes";
import Loader from "../../../control-components/loader/loader";
import { NotificationManager } from "src/util/notify";
import NOTIFICATION_MESSAGE from "../../../helper/notification-messages";
import { SubpoenaRequestService } from "../../../services/subpoenaRequestService";
import DropDownKendoRct from "../../../control-components/drop-down/drop-down";
import ApiHelper from "../../../helper/api-helper";
import ApiUrls from "../../../helper/api-urls";
import {
  GET_SUBPOENA_REQUESTS,
  SELECTED_SUBPOENA_REGION_FILTER,
} from "../../../actions";

const DATA_ITEM_KEY = "id";
const DEFAULT_LIST_PAYLOAD = {
  clinicId: null,
  isActive: true,
};

const SUBPOENA_STATUS = {
  PENDING: 1,
  IN_PROGRESS: 2,
  ON_HOLD: 3,
  COMPLETED: 4,
};

const STATUS_OPTIONS = [
  { id: 1, name: "Pending" },
  { id: 2, name: "In Progress" },
  { id: 3, name: "On Hold" },
  { id: 4, name: "Completed" },
];

const SubpoenaRequestList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const storedRequests = useSelector((state) => state.subpoenaRequests);
  const regionData = useSelector((state) => state.getRegion);
  const persistedRegionFilter = useSelector((state) => state.subpoenaRegionFilter);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataState, setDataState] = useState({
    skip: 0,
    take: 10,
    sort: [],
  });
  const [refreshToggle, setRefreshToggle] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [filters, setFilters] = useState({
    region: null,
    clinic: null,
  });
  const [allClinics, setAllClinics] = useState([]);
  const [filteredClinics, setFilteredClinics] = useState([]);
  const [gridRefreshKey, setGridRefreshKey] = useState(0);
  const [expandedModules, setExpandedModules] = useState({});
  const [expandedClients, setExpandedClients] = useState({});

  useEffect(() => {
    fetchAllClinics();
  }, []);

  useEffect(() => {
    if (regionData && regionData.length > 0) {
      // Load from persisted filter if available
      if (persistedRegionFilter && persistedRegionFilter.region) {
        setFilters((prev) => ({
          ...prev,
          region: persistedRegionFilter.region,
        }));
      } else {
        const defaultRegion = {
          id: regionData[0]?.id,
          regionName: regionData[0]?.regionName,
        };
        setFilters((prev) => ({
          ...prev,
          region: defaultRegion,
        }));
      }
    }
  }, [regionData]);

  useEffect(() => {
    if (filters.region?.id && allClinics.length > 0) {
      const clinicsForRegion = allClinics.filter(
        (clinic) => clinic.regionId === filters.region.id
      );
      setFilteredClinics(clinicsForRegion);
      if (filters.clinic && !clinicsForRegion.find(c => c.id === filters.clinic.id)) {
        setFilters((prev) => ({ ...prev, clinic: null }));
      }
    } else {
      setFilteredClinics([]);
      setFilters((prev) => ({ ...prev, clinic: null }));
    }
  }, [filters.region?.id, allClinics]);

  useEffect(() => {
    if (filters.region?.id) {
      setDataState((prev) => ({
        ...prev,
        skip: 0,
      }));
      fetchRequests();
    }
  }, [refreshToggle, filters.region?.id, filters.clinic?.id]);

  useEffect(() => {
    if (Array.isArray(storedRequests)) {
      setRequests(storedRequests);
    }
  }, [storedRequests]);

  useEffect(() => {
    setGridRefreshKey(prev => prev + 1);
  }, [requests.length]);

  const fetchAllClinics = async () => {
    try {
      const response = await ApiHelper.getRequest(ApiUrls.ALL_CLINIC);
      const clinicList = (response?.resultData || []).map((clinic) => ({
        ...clinic,
        clinicName: clinic.clinicName || clinic.name,
      }));
      setAllClinics(clinicList);
    } catch (error) {
      NotificationManager.error(error?.message || "Unable to load clinics.");
      setAllClinics([]);
    }
  };

  const fetchRequests = async () => {
    if (!filters.region?.id) {
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...DEFAULT_LIST_PAYLOAD,
        regionId: filters.region.id,
        clinicId: filters.clinic?.id || null,
      };
      const response = await SubpoenaRequestService.getSubpoenaRequests(payload);
      const result = response?.resultData || [];
      dispatch({ type: GET_SUBPOENA_REQUESTS, payload: result });

      setRequests(result);
    } catch (error) {
      NOTIFICATION_MESSAGE.error(error);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    if (name === "region" && !value) {
      return;
    }

    const updatedFilters = {
      ...filters,
      [name]: value,
      ...(name === "region" ? { clinic: null } : {}),
    };

    setFilters(updatedFilters);

    // Persist region filter to Redux
    if (name === "region") {
      dispatch({
        type: SELECTED_SUBPOENA_REGION_FILTER,
        payload: {
          region: value,
        },
      });
    }
  };

  const gridData = useMemo(() => {
    const sorted = orderBy(requests, dataState.sort);
    const paginated = sorted.slice(
      dataState.skip,
      dataState.skip + dataState.take
    );
    return paginated;
  }, [requests, dataState]);

  const handleDataStateChange = (event) => {
    setDataState(event.dataState);
  };

  const handleAdd = () => {
    navigate(APP_ROUTES.ADD_SUBPOENA_REQUEST);
  };

  const resolveRequestId = (request) => request?.id;

  const handleDelete = async (request) => {
    if (!request.regionId) {
      NotificationManager.error("Unable to delete. Region information is missing.");
      return;
    }

    setLoading(true);
    try {
      await SubpoenaRequestService.deleteSubpoenaRequest({
        subpoenaRequestId: resolveRequestId(request),
        regionId: request.regionId,
      });
      NotificationManager.success("Subpoena request deleted.");
      setRefreshToggle((prev) => !prev);
    } catch (error) {
      NOTIFICATION_MESSAGE.error(error);
      setLoading(false);
    }
  };

  const handleDownload = async (request) => {
    
    setLoading(true);
    try {
      const payload = {
        subpoenaRequestId: request.id,
        clinicId: request.clinicId,
        regionId: request.regionId,
      };
      const response = await SubpoenaRequestService.getSubpoenaRequestZipUrl(payload);
      
      const presignedUrl = response?.resultData?.presignedUrl;
      
      if (presignedUrl) {
        window.open(presignedUrl, "_blank");
      } else {
        NotificationManager.info("Download link is not available yet.");
      }
    } catch (error) {
      NOTIFICATION_MESSAGE.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenStatusModal = (request) => {
    setSelectedRequest(request);
    setSelectedStatus(request.statusId);
    setShowStatusModal(true);
  };

  const handleCloseStatusModal = () => {
    setShowStatusModal(false);
    setSelectedRequest(null);
    setSelectedStatus(null);
  };

  const handleStatusChange = async () => {
    if (!selectedRequest || !selectedStatus) {
      NotificationManager.error("Please select a status.");
      return;
    }

    setLoading(true);
    try {
      await SubpoenaRequestService.updateSubpoenaRequestStatus({
        subpoenaRequestId: resolveRequestId(selectedRequest),
        statusId: selectedStatus,
        regionId: selectedRequest.regionId,
      });
      NotificationManager.success("Status updated successfully.");
      handleCloseStatusModal();
      setRefreshToggle((prev) => !prev);
    } catch (error) {
      NOTIFICATION_MESSAGE.error(error);
      setLoading(false);
    }
  };

  const handleEdit = (request) => {
    navigate(APP_ROUTES.ADD_SUBPOENA_REQUEST, {
      state: { ...request, regionId: filters.region?.id },
    });
  };

  const handleResendEmail = async (request) => {
    if (!request || !request.regionId) {
      NotificationManager.error("Unable to send email. Missing required information.");
      return;
    }

    setLoading(true);
    try {
      await SubpoenaRequestService.sendSubpoenaLinkEmail(
        resolveRequestId(request),
        request.regionId
      );
      NotificationManager.success("Email sent successfully.");
    } catch (error) {
      NOTIFICATION_MESSAGE.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="address-title text-grey mb-0">
          <span className="f-24">Subpoena Requests</span>
        </h4>
        <button className="green-btn btn-primary" onClick={handleAdd}>
          <span className="fa fa-plus mr-2" style={{ color: "#fff" }}></span>
          Add New Request
        </button>
      </div>

      <div className="d-flex flex-wrap gap-3 mb-3">
        <div className="col-lg-3 col-md-4 col-12 edit-Service-popup" style={{ paddingLeft: "0px" }}>
          <DropDownKendoRct
            filterable={true}
            label="Region"
            onChange={handleFilterChange}
            data={regionData}
            value={filters.region}
            textField="regionName"
            name="region"
            dataItemKey="id"
            suggest={true}
            required
          />
        </div>
        <div className="col-lg-3 col-md-4 col-12 edit-Service-popup" style={{ paddingLeft: "0px" }}>
          <DropDownKendoRct
            filterable={true}
            label="Clinic"
            onChange={handleFilterChange}
            data={filteredClinics}
            value={filters.clinic}
            textField="clinicName"
            name="clinic"
            dataItemKey="id"
            suggest={true}
            disabled={!filters.region}
          />
        </div>
      </div>

      {loading ? <Loader /> :
        <div className="grid-table filter-grid header-blue">
          <div className="inner-section-edit position-relative">
            <Grid
              key={`grid-refresh-${gridRefreshKey}`}
              data={gridData}
              dataItemKey={DATA_ITEM_KEY}
              sortable
              sort={dataState.sort}
              skip={dataState.skip}
              take={dataState.take}
              total={requests.length}
              onDataStateChange={handleDataStateChange}
              pageable={{
                pageSizes: [5, 10, 20],
                buttonCount: 3,
              }}
            >
              <GridColumn
                field="id"
                title="Request #"
                width="120px"
                headerClassName="text-left"
                className="text-left"
              />
              <GridColumn
                field="clinicName"
                title="Clinic"
                width="220px"
                headerClassName="text-left"
                className="text-left"
              />
              <GridColumn
                title="Modules Requested"
                width="260px"
                headerClassName="text-left"
                className="text-left text-break"
                cells={{
                  data: (props) => {
                    const modules = Array.isArray(props.dataItem.modules)
                      ? props.dataItem.modules
                      : [];
                    const requestId = props.dataItem.id;
                    const isExpanded = expandedModules[requestId];
                    const hasMore = modules.length > 3;
                    const displayModules = hasMore && !isExpanded ? modules.slice(0, 3) : modules;

                    if (modules.length === 0) {
                      return <td className="text-left" style={{ width: "260px" }}>-</td>;
                    }

                    return (
                      <td className="text-left" style={{ width: "260px" }}>
                        <div className="d-flex flex-column">
                          {displayModules.map((module, index) => (
                            <span 
                              key={index} 
                              className="d-flex align-items-center"
                              style={{ marginBottom: index < displayModules.length - 1 ? "8px" : "0" }}
                            >
                              <i 
                                className="fa fa-check-circle mr-2" 
                                style={{ 
                                  color: module.isProcessed ? "#28a745" : "#999", 
                                  fontSize: "16px" 
                                }}
                                title={module.isProcessed ? "Processed" : "Not Processed"}
                              ></i>
                              {module.moduleName}
                            </span>
                          ))}
                          {hasMore && (
                            <span 
                              className="d-flex align-items-center mt-2"
                              style={{ cursor: "pointer", color: "#007bff", fontSize: "14px" }}
                              onClick={() => setExpandedModules(prev => ({ ...prev, [requestId]: !isExpanded }))}
                            >
                              <i className={`fa ${isExpanded ? "fa-minus" : "fa-plus"} mr-1`}></i>
                              {isExpanded ? "Show Less" : `Show All (${modules.length})`}
                            </span>
                          )}
                        </div>
                      </td>
                    );
                  }
                }}
              />
              <GridColumn
                title="Clients"
                width="240px"
                headerClassName="text-left"
                className="text-left text-break"
                cells={{
                  data: (props) => {
                    const clients = Array.isArray(props.dataItem.clients)
                      ? props.dataItem.clients
                      : [];
                    const requestId = props.dataItem.id;
                    const isExpanded = expandedClients[requestId];
                    const hasMore = clients.length > 3;
                    const displayClients = hasMore && !isExpanded ? clients.slice(0, 3) : clients;

                    if (clients.length === 0) {
                      return <td className="text-left" style={{ width: "240px" }}>-</td>;
                    }
                    return (
                      <td className="text-left" style={{ width: "240px" }}>
                        <div className="d-flex flex-column">
                          {displayClients.map((client, index) => {
                            const lastName = client.clientLastName || "";
                            const firstName = client.clientFirstName || "";
                            const displayName = lastName && firstName
                              ? `${lastName}, ${firstName}`
                              : lastName || firstName || "-";
                            return <span key={index}>{displayName}</span>;
                          })}
                          {hasMore && (
                            <span 
                              className="d-flex align-items-center mt-2"
                              style={{ cursor: "pointer", color: "#007bff", fontSize: "14px" }}
                              onClick={() => setExpandedClients(prev => ({ ...prev, [requestId]: !isExpanded }))}
                            >
                              <i className={`fa ${isExpanded ? "fa-minus" : "fa-plus"} mr-1`}></i>
                              {isExpanded ? "Show Less" : `Show All (${clients.length})`}
                            </span>
                          )}
                        </div>
                      </td>
                    );
                  }
                }}
              />
              <GridColumn
                field="startDate"
                title="Start Date"
                width="160px"
                headerClassName="text-left"
                className="text-left"
                cells={{
                  data: (props) => {
                    return (
                      <td className="text-left" style={{ width: "160px" }}>
                        {props.dataItem.startDate &&
                          props.dataItem.startDate != null
                          ? moment(props.dataItem.startDate)
                            .local()
                            .format("M/D/YYYY")
                          : "-"}
                      </td>
                    );
                  }
                }}
              />
              <GridColumn
                field="endDate"
                title="End Date"
                width="160px"
                headerClassName="text-left"
                className="text-left"
                cells={{
                  data: (props) => {
                    return (
                      <td className="text-left" style={{ width: "160px" }}>
                        {props.dataItem.endDate &&
                          props.dataItem.endDate != null
                          ? moment(props.dataItem.endDate)
                            .local()
                            .format("M/D/YYYY")
                          : "-"}
                      </td>
                    );
                  }
                }}
              />
              <GridColumn
                field="dueDate"
                title="Due Date"
                width="160px"
                headerClassName="text-left"
                className="text-left"
                cells={{
                  data: (props) => {
                    return (
                      <td className="text-left" style={{ width: "160px" }}>
                        {props.dataItem.dueDate &&
                          props.dataItem.dueDate != null
                          ? moment(props.dataItem.dueDate)
                            .local()
                            .format("M/D/YYYY")
                          : "-"}
                      </td>
                    );
                  }
                }}
              />
              <GridColumn
                field="emailAddress"
                title="Email"
                width="240px"
                headerClassName="text-left"
                className="text-left text-break"
              />
              <GridColumn
                field="phoneNumber"
                title="Mobile"
                width="180px"
                headerClassName="text-left"
                className="text-left"
              />
              <GridColumn
                field="statusId"
                title="Status"
                width="140px"
                headerClassName="text-left"
                className="text-left"
                cells={{
                  data: (props) => {
                    const statusId = props.dataItem.statusId;
                    const status = STATUS_OPTIONS.find((s) => s.id === statusId);
                    return (
                      <td className="text-left" style={{ width: "140px" }}>
                        {status ? status.name : "-"}
                      </td>
                    );
                  }
                }}
              />
              <GridColumn
                width="300px"
                title="Actions"
                cells={{
                  data: (props) => {
                    const request = props.dataItem;
                    const statusId = request?.statusId;
                    const isPending = statusId === SUBPOENA_STATUS.PENDING;

                    return (
                      <td style={{ width: "300px" }}>
                        <div className="row-3">
                          {isPending && (
                            <div
                              className="k-chip k-chip-md k-rounded-md k-chip-solid k-chip-solid-base icon-red"
                              onClick={() => handleDelete(request)}
                            >
                              <div className="k-chip-content">
                                <Tooltip anchorElement="target" position="top">
                                  <i
                                    className="fa fa-trash"
                                    aria-hidden="true"
                                    title="Delete Request"
                                  ></i>
                                </Tooltip>
                              </div>
                            </div>
                          )}
                          {isPending && (
                            <div
                              className="k-chip k-chip-md k-rounded-md k-chip-solid k-chip-solid-base icon-green m-2"
                              onClick={() => handleEdit(request)}
                            >
                              <div className="k-chip-content">
                                <Tooltip anchorElement="target" position="top">
                                  <i className="fas fa-edit" title="Edit"></i>
                                </Tooltip>
                              </div>
                            </div>
                          )}
                          {statusId === SUBPOENA_STATUS.COMPLETED && (
                            <div
                              className="k-chip k-chip-md k-rounded-md k-chip-solid k-chip-solid-base icon-blue m-2"
                              onClick={() => handleDownload(request)}
                            >
                              <div className="k-chip-content">
                                <Tooltip anchorElement="target" position="top">
                                  <i className="fa fa-download" title="Download"></i>
                                </Tooltip>
                              </div>
                            </div>
                          )}
                          <div
                            className="k-chip k-chip-md k-rounded-md k-chip-solid k-chip-solid-base icon-warning m-2"
                            onClick={() => handleOpenStatusModal(request)}
                          >
                            <div className="k-chip-content">
                              <Tooltip anchorElement="target" position="top">
                                <i className="fa fa-tasks" title="Edit Status"></i>
                              </Tooltip>
                            </div>
                          </div>
                          {statusId === SUBPOENA_STATUS.COMPLETED && (
                            <div
                              className="k-chip k-chip-md k-rounded-md k-chip-solid k-chip-solid-base icon-blue m-2"
                              onClick={() => handleResendEmail(request)}
                            >
                              <div className="k-chip-content">
                                <Tooltip anchorElement="target" position="top">
                                  <i className="fa fa-envelope" title="Resend Email"></i>
                                </Tooltip>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    );
                  }
                }}
              />
            </Grid>
          </div>
        </div>}

      {showStatusModal && (
        <Dialog title="Update Status" onClose={handleCloseStatusModal} width={400}>
          <div className="p-4">
            <div className="mb-3">
              <label className="form-label" style={{ fontSize: "0.875rem", fontWeight: "600" }}>
                Select Status
              </label>
              <div className="status-options mt-2">
                {STATUS_OPTIONS.map((status) => (
                  <label
                    key={status.id}
                    className="form-check-label d-block mb-3"
                    style={{ cursor: "pointer", paddingLeft: "1.75rem" }}
                  >
                    <input
                      type="radio"
                      name="status"
                      className="form-check-input"
                      checked={selectedStatus === status.id}
                      onChange={() => setSelectedStatus(status.id)}
                      style={{ marginLeft: "-1.75rem" }}
                    />
                    <span style={{ marginLeft: "0.5rem" }}>{status.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogActionsBar>
            <button
              className="btn grey-secondary btn-space"
              onClick={handleCloseStatusModal}
            >
              Cancel
            </button>
            <button
              className="green-btn btn-primary btn-space"
              onClick={handleStatusChange}
            >
              Change Status
            </button>
          </DialogActionsBar>
        </Dialog>
      )}
    </>
  );
};

export default SubpoenaRequestList;

